// src/layouts/UserLayout.tsx
//
// Shared layout for parent and student portals.
// The sidebar nav is built at runtime from the user's role — parents see
// child-management nav, older students see their own builder nav, and
// younger students (ages 6–12) see a simplified, parent-mediated view.
// A learner (a child 9+ on the login their parent set up) sees only their
// own lessons; every other page sends them back there.
//
import { useState, useEffect, useRef } from "react";
import SharedBreadcrumb from "../components/Breadcrumb";
import NotificationBell from "../components/NotificationBell";
import {
  Link,
  Navigate,
  NavLink,
  Outlet,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  selectUser,
  selectRole,
  selectHydrated,
  clearCredentials,
} from "../app/features/authSlice";
import { useLogoutMutation } from "../app/api/authApi";
import { useChildMode } from "../app/childMode";
import LearnLayout from "./LearnLayout";

import { BookOpenCheck, CalendarDays, ChartLine, ClipboardCheck, CreditCard, LayoutDashboard, MessagesSquare, Package, Settings, Users } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import Ico from "../components/Ico";
/* ─────────────────────────────────────────────────────────────────
   NAV DEFINITIONS
   Each item can be scoped to one or more roles.
   "parentOnly"  — shown only when role === "parent"
   "studentOnly" — shown only when role === "student"
   omitting both — shown to all (parent + student)
───────────────────────────────────────────────────────────────── */

interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  parentOnly?: boolean;
  studentOnly?: boolean;
  /** Hide from young students (ages 6–12) who need parent mediation */
  seniorStudentOnly?: boolean;
  badge?: string;
}

const NAV_ITEMS: NavItem[] = [
  // ── Shared ──────────────────────────────────────────────────
  {
    label: "Dashboard",
    to: "/user/dashboard",
    icon: LayoutDashboard,
    parentOnly: true,
  },
  {
    label: "Learn",
    to: "/user/learn",
    icon: BookOpenCheck,
  },

  // ── Parent-only ──────────────────────────────────────────────
  {
    label: "My Children",
    to: "/user/children",
    icon: Users,
    parentOnly: true,
  },
  {
    label: "Enrolments",
    to: "/user/enrolments",
    icon: ClipboardCheck,
    parentOnly: true,
  },
  {
    label: "Payments",
    to: "/user/payments",
    icon: CreditCard,
    parentOnly: true,
  },
  {
    label: "My Kit",
    to: "/user/kits",
    icon: Package,
    parentOnly: true,
  },
  {
    label: "Showcase Dates",
    to: "/user/showcases",
    icon: CalendarDays,
    parentOnly: true,
  },
  {
    label: "Progress Reports",
    to: "/user/reports",
    icon: ChartLine,
    parentOnly: true,
  },
  {
    label: "Messages",
    to: "/user/messages",
    icon: MessagesSquare,
    parentOnly: true,
  },

  // ── Shared (both roles) ───────────────────────────────────────
  {
    label: "Settings",
    to: "/user/settings",
    icon: Settings,
  },
];

/* ─────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────── */

/**
 * Determine whether a logged-in student is "senior" (13–18) or "junior" (6–12).
 * In production this would come from the student's profile (age or track).
 * We expose a best-effort fallback here using a field the backend could add.
 */
function isSeniorStudent(user: ReturnType<typeof selectUser>): boolean {
  // If the backend eventually adds `age` or `track` to AuthUser, use that.
  // For now we treat all students as junior by default and let the profile
  // page provide an override stored locally — or the admin can tag them.
  // Change this logic once you have an age/track field on the user object.
  return false; // conservative default — parents handle most actions
}

/** The only page a learner session opens: their own lessons. */
const learnerHome = (user: ReturnType<typeof selectUser>) =>
  `/user/learn/${user?.childId ?? ""}`;

function getVisibleItems(
  role: string | undefined,
  senior: boolean,
  user?: ReturnType<typeof selectUser>,
): NavItem[] {
  if (!role) return [];
  if (role === "learner") {
    return [{ label: "My lessons", to: learnerHome(user), icon: BookOpenCheck }];
  }
  return NAV_ITEMS.filter((item) => {
    if (item.parentOnly && role !== "parent") return false;
    if (item.studentOnly && role !== "student") return false;
    if (item.seniorStudentOnly && !senior) return false;
    return true;
  });
}

const routeLabels: Record<string, string> = {
  learn: "Learn",
  kits: "My Kit",
  showcase: "Showcase",
  certificate: "Certificate",
  user: "Portal",
  dashboard: "Dashboard",
  children: "My Children",
  enrolments: "Enrolments",
  payments: "Payments",
  showcases: "Showcases",
  reports: "Progress Reports",
  messages: "Messages",
  progress: "My Progress",
  projects: "My Projects",
  "showcase-prep": "Showcase Prep",
  resources: "Resources",
  "pitch-deck": "Pitch Deck",
  venture: "Venture Tools",
  settings: "Settings",
};

/* ─────────────────────────────────────────────────────────────────
   BREADCRUMB
───────────────────────────────────────────────────────────────── */

function Breadcrumb() {
  return <SharedBreadcrumb labels={routeLabels} root="user" />;
}

/* ─────────────────────────────────────────────────────────────────
   ROLE BADGE
───────────────────────────────────────────────────────────────── */

function RoleBadge({ role }: { role: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    parent: {
      label: "Parent",
      cls: "bg-[#fff4f1] text-[#7a3020] border border-[#f5c5b0]",
    },
    student: {
      label: "Student",
      cls: "bg-[#ebeff7] text-[#4a44b0] border border-[#cdd4e2]",
    },
    learner: {
      label: "Learner",
      cls: "bg-[#ebeff7] text-[#4a44b0] border border-[#cdd4e2]",
    },
  };
  const s = map[role] ?? {
    label: role,
    cls: "bg-[#ebeff7] text-[#4a44b0] border border-[#dde2ec]",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold uppercase tracking-wider ${s.cls}`}
    >
      {s.label}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────────
   USER MENU (top-right dropdown)
───────────────────────────────────────────────────────────────── */

function UserMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const [logout] = useLogoutMutation();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = async () => {
    try {
      await logout().unwrap();
    } catch {
      // continue regardless — token is stateless
    }
    dispatch(clearCredentials());
    navigate("/login");
  };

  const initials = user
    ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
    : "?";

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2.5 rounded-2xl border border-[#dde2ec] bg-white px-3 py-2 text-sm text-[#16213e] transition hover:border-[#c9cfe0] hover:bg-[#f9fafc]"
      >
        <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-xl bg-[#1b2d5e] text-xs font-bold text-white">
          {user?.avatar ? (
            <img
              src={user.avatar}
              alt={initials}
              className="h-7 w-7 rounded-xl object-cover"
            />
          ) : (
            initials
          )}
        </span>
        <span className="hidden max-w-[120px] truncate font-medium sm:block">
          {user?.firstName}
        </span>
        <svg
          className={`h-3.5 w-3.5 text-[#8189a0] transition-transform ${open ? "rotate-180" : ""}`}
          viewBox="0 0 12 12"
          fill="none"
        >
          <path
            d="M2 4l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-2xl border border-[#dde2ec] bg-white shadow-[0_16px_48px_rgba(27,45,94,0.14)]">
          {/* Identity block */}
          <div className="border-b border-[#ebeff7] bg-[#f9fafc] px-4 py-4">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#1b2d5e] text-sm font-bold text-white">
                {initials}
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-[#16213e]">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="truncate text-xs text-[#8189a0]">
                  {user?.role === "learner" ? "Signed in to learn" : user?.email}
                </p>
              </div>
            </div>
            {user?.role && (
              <div className="mt-3">
                <RoleBadge role={user.role} />
              </div>
            )}
          </div>

          {/* Links (a learner's password is managed by their parent) */}
          {user?.role !== "learner" && (
          <div className="p-1.5">
            {[
              ...(user?.role === "parent" ? [{ label: "My Profile", to: "/user/settings#profile" }] : []),
              { label: "Change Password", to: "/user/settings#password" },
            ].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-[#3b4560] transition hover:bg-[#f1f3f8]"
              >
                {item.label}
              </Link>
            ))}
          </div>
          )}

          <div className="border-t border-[#ebeff7] p-1.5">
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-[#c0392b] transition hover:bg-[#fff5f5]"
            >
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   SIDEBAR NAV ITEM
───────────────────────────────────────────────────────────────── */

function SideNavItem({
  item,
  collapsed,
  onClick,
}: {
  item: NavItem;
  collapsed: boolean;
  onClick?: () => void;
}) {
  return (
    <NavLink
      to={item.to}
      end={item.to === "/user/dashboard"}
      onClick={onClick}
      className={({ isActive }) =>
        [
          "group flex items-center gap-3 rounded-[14px] px-3 py-2.5 text-sm transition-all",
          isActive
            ? "bg-[#1b2d5e] font-semibold text-white shadow-[0_4px_16px_rgba(27,45,94,0.30)]"
            : "text-[#5a6278] hover:bg-[#ebeff7] hover:text-[#16213e]",
        ].join(" ")
      }
      title={collapsed ? item.label : undefined}
    >
      {({ isActive }) => (
        <>
          <span
            className={`flex-shrink-0 text-base leading-none transition-transform group-hover:scale-110 ${
              isActive ? "text-white" : "text-[#1b2d5e]"
            }`}
          >
            <Ico icon={item.icon} />
          </span>
          {!collapsed && (
            <span className="flex-1 truncate leading-none">{item.label}</span>
          )}
          {!collapsed && item.badge && (
            <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#efb0a4] px-1.5 text-[11.5px] font-bold text-[#5b2e28]">
              {item.badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

/* ─────────────────────────────────────────────────────────────────
   SIDEBAR
───────────────────────────────────────────────────────────────── */

interface SidebarProps {
  collapsed: boolean;
  onClose?: () => void;
}

function Sidebar({ collapsed, onClose }: SidebarProps) {
  const user = useSelector(selectUser);
  const role = useSelector(selectRole);
  const senior = isSeniorStudent(user);
  const visibleItems = getVisibleItems(role, senior, user);

  // Group parent nav for a cleaner sidebar
  const isParent = role === "parent";

  const parentGroups = isParent
    ? [
        {
          label: "Overview",
          items: visibleItems.filter((i) => ["/user/dashboard", "/user/learn"].includes(i.to)),
        },
        {
          label: "Children",
          items: visibleItems.filter((i) =>
            ["/user/children", "/user/enrolments", "/user/reports"].includes(
              i.to,
            ),
          ),
        },
        {
          label: "Academy",
          items: visibleItems.filter((i) =>
            ["/user/kits", "/user/showcases", "/user/messages"].includes(i.to),
          ),
        },
        {
          label: "Billing",
          items: visibleItems.filter((i) => ["/user/payments"].includes(i.to)),
        },
        {
          label: "Account",
          items: visibleItems.filter((i) => ["/user/settings"].includes(i.to)),
        },
      ].filter((g) => g.items.length > 0)
    : [];

  return (
    <aside
      className={[
        "flex h-full flex-col border-r border-[#dde2ec] bg-white transition-all duration-300",
        collapsed ? "w-[68px]" : "w-[240px]",
      ].join(" ")}
    >
      {/* Logo */}
      <div
        className={[
          "flex h-16 flex-shrink-0 items-center border-b border-[#ebeff7]",
          collapsed ? "justify-center px-3" : "gap-2.5 px-5",
        ].join(" ")}
      >
        <img src="/images/logo.png" alt="" className="h-8 w-8 flex-shrink-0 rounded-xl" />
        {!collapsed && (
          <div className="min-w-0">
            <p className="truncate text-[13px] font-bold leading-tight tracking-[-0.03em] text-[#16213e]">
              Future Smart Lab
            </p>
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-[#8189a0]">
              {role === "parent" ? "Parent Portal" : "Student Portal"}
            </p>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-2.5 py-4">
        {isParent && !collapsed ? (
          // Grouped nav for parents
          <div className="space-y-5">
            {parentGroups.map((group) => (
              <div key={group.label}>
                <p className="mb-1.5 px-3 text-[11px] font-bold uppercase tracking-[0.2em] text-[#c9cfe0]">
                  {group.label}
                </p>
                <div className="space-y-0.5">
                  {group.items.map((item) => (
                    <SideNavItem
                      key={item.to}
                      item={item}
                      collapsed={false}
                      onClick={onClose}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Flat nav for students or collapsed state
          <div className="space-y-0.5">
            {visibleItems.map((item) => (
              <SideNavItem
                key={item.to}
                item={item}
                collapsed={collapsed}
                onClick={onClose}
              />
            ))}
          </div>
        )}
      </nav>

      {/* Bottom identity strip */}
      {!collapsed && (
        <div className="border-t border-[#ebeff7] px-4 py-4">
          <div className="flex items-center gap-2.5">
            <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-xl bg-[#1b2d5e] text-[11.5px] font-bold text-white">
              {user?.firstName?.charAt(0)}
              {user?.lastName?.charAt(0)}
            </span>
            <div className="min-w-0">
              <p className="truncate text-[12px] font-semibold text-[#16213e]">
                {user?.firstName} {user?.lastName}
              </p>
              {user?.role && <RoleBadge role={user.role} />}
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}

/* ─────────────────────────────────────────────────────────────────
   MOBILE DRAWER
───────────────────────────────────────────────────────────────── */

function MobileDrawer({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  if (!open) return null;
  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-[#16213e]/40 backdrop-blur-sm lg:hidden"
        onClick={onClose}
      />
      <div className="fixed inset-y-0 left-0 z-50 flex lg:hidden">
        <Sidebar collapsed={false} onClose={onClose} />
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────
   TOPBAR
───────────────────────────────────────────────────────────────── */

function Topbar({
  sidebarCollapsed,
  onToggleSidebar,
  onOpenMobileDrawer,
}: {
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  onOpenMobileDrawer: () => void;
}) {
  // Learner sessions only reach /api/learn, so they get no bell
  const isLearner = useSelector(selectUser)?.role === "learner";
  return (
    <header className="flex h-16 flex-shrink-0 items-center justify-between border-b border-[#dde2ec] bg-white px-4 sm:px-6">
      <div className="flex items-center gap-3">
        {/* Mobile hamburger */}
        <button
          onClick={onOpenMobileDrawer}
          className="rounded-xl border border-[#dde2ec] p-2 text-[#5a6278] transition hover:bg-[#f1f3f8] lg:hidden"
          aria-label="Open menu"
        >
          <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
            <path
              d="M2 4h12M2 8h12M2 12h12"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {/* Desktop collapse toggle */}
        <button
          onClick={onToggleSidebar}
          className="hidden rounded-xl border border-[#dde2ec] p-2 text-[#5a6278] transition hover:bg-[#f1f3f8] lg:flex"
          aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <svg
            className={`h-4 w-4 transition-transform ${sidebarCollapsed ? "rotate-180" : ""}`}
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M10 4L6 8l4 4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <Breadcrumb />
      </div>

      <div className="flex items-center gap-2.5">
        <NotificationBell skip={isLearner} />

        <UserMenu />
      </div>
    </header>
  );
}

/* ─────────────────────────────────────────────────────────────────
   USER LAYOUT — root export
───────────────────────────────────────────────────────────────── */

export default function UserLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const location = useLocation();
  const { pathname } = location;
  const user = useSelector(selectUser);
  const hydrated = useSelector(selectHydrated);
  const childMode = useChildMode();

  const mainRef = useRef<HTMLElement>(null);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileDrawerOpen(false);
  }, [pathname]);

  // The page area scrolls on its own, so a new page starts at the top
  // (links to a section, like #password, scroll there instead)
  useEffect(() => {
    if (!location.hash) mainRef.current?.scrollTo(0, 0);
  }, [pathname, location.hash]);

  // No session (signed out, expired, or ended by the server) → sign in,
  // then come back here
  if (hydrated && !user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Child mode (a parent's device handed to a young child): only that child's lessons
  if (user?.role === "parent" && childMode) {
    const kidHome = `/user/learn/${childMode.childId}`;
    if (pathname !== kidHome && !pathname.startsWith(`${kidHome}/`)) {
      return <Navigate to={kidHome} replace />;
    }
    return <LearnLayout child={childMode} />;
  }

  // A learner session only opens their own lessons
  const home = learnerHome(user);
  if (user?.role === "learner" && pathname !== home && !pathname.startsWith(`${home}/`)) {
    return <Navigate to={home} replace />;
  }

  // Lessons get the whole screen: students and learners always, parents as
  // soon as they open Learn
  if (
    user?.role === "student" ||
    user?.role === "learner" ||
    pathname === "/user/learn" ||
    pathname.startsWith("/user/learn/")
  ) {
    return <LearnLayout />;
  }

  return (
    <div className="flex h-screen overflow-hidden bg-[#f1f3f8] font-sans antialiased">
      {/* Desktop sidebar */}
      <div className="hidden flex-shrink-0 lg:flex">
        <Sidebar collapsed={sidebarCollapsed} />
      </div>

      {/* Mobile drawer */}
      <MobileDrawer
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
      />

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Topbar
          sidebarCollapsed={sidebarCollapsed}
          onToggleSidebar={() => setSidebarCollapsed((v) => !v)}
          onOpenMobileDrawer={() => setMobileDrawerOpen(true)}
        />

        <main ref={mainRef} className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
